import React, { useEffect, useMemo, useState } from "react";
import { api, getSessionId } from "../lib/api";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ChevronRight, Terminal, Gauge, RefreshCw } from "lucide-react";
import { MobileAdvisor } from "../components/MobileAdvisor";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adSlots";

// Each step contains: id, title, quick (non-tech), detail (what / why)
const BASE_STEPS = [
  {
    id: "check-cables",
    title: "Check the cables and power",
    quick: "Unplug your router, wait 30 seconds, then plug it back in. Make sure the Ethernet cable to the modem is seated.",
    detail: "A full power-cycle clears stale DHCP leases and resets the DSL/cable line card. Wait for all lights (Power, DSL/Cable, Internet) to turn solid green before testing.",
  },
  {
    id: "wifi-vs-wired",
    title: "Eliminate Wi-Fi as the suspect",
    quick: "If you can, plug your computer directly into the router with an Ethernet cable and re-test.",
    detail: "Wired connections bypass radio interference, bandsteering and mesh backhaul issues. If wired is fast but Wi-Fi is slow, the problem is your wireless, not your ISP.",
  },
  {
    id: "reposition",
    title: "Move closer to the router",
    quick: "Wi-Fi drops off fast through walls. Try the test within sight of the router.",
    detail: "Each solid wall can drop signal by 10–15 dB. Line-of-sight at under 5m typically yields the best throughput. If this fixes it, consider a mesh or access point.",
  },
  {
    id: "dns-switch",
    title: "Try a faster DNS",
    quick: "Slow page loads but fast downloads? Switch DNS to 1.1.1.1 or 8.8.8.8.",
    detail: "DNS resolution happens before every new domain. Your ISP's resolver may be slow or logging. Cloudflare (1.1.1.1) and Google (8.8.8.8) are usually faster and respect privacy better.",
  },
  {
    id: "background-hogs",
    title: "Close bandwidth hogs",
    quick: "Pause cloud sync, video calls, and game updates. Re-run the test.",
    detail: "OneDrive/Dropbox/iCloud uploads, Steam/PS5/Xbox updates and security scans can saturate your uplink. Check your OS Activity Monitor / Task Manager for top network consumers.",
  },
  {
    id: "isp-outage",
    title: "Is your ISP having a bad day?",
    quick: "Open the Down Detector tab — if major services are red in your area, it's not you.",
    detail: "Residential ISP outages often correlate with upstream issues at Cloudflare, Akamai or a transit provider. Check the status page of your ISP and the Down Detector below.",
  },
  {
    id: "call-isp",
    title: "Still bad? Call your ISP",
    quick: "Give them your ping, download and upload numbers. Ask them to 'refresh the line profile.'",
    detail: "Armed with concrete numbers (not 'internet is slow') you'll skip 20 minutes of script. Request the technician check your SNR margin, attenuation and line errors.",
  },
];

export default function FixMyInternetPage() {
  const [latest, setLatest] = useState(null);
  const [completed, setCompleted] = useState({});
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    api.get("/speedtest/history", { params: { session_id: getSessionId(), limit: 1 } })
      .then((r) => setLatest((r.data && r.data[0]) || null))
      .catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem("stt.fixSteps") || "{}");
      setCompleted(saved);
    } catch {}
  }, []);

  const toggle = (id) => {
    const next = { ...completed, [id]: !completed[id] };
    setCompleted(next);
    localStorage.setItem("stt.fixSteps", JSON.stringify(next));
  };

  // Recommend an order based on the latest test
  const steps = useMemo(() => {
    if (!latest) return BASE_STEPS;
    const order = [...BASE_STEPS];
    // If ping is high but download okay → DNS + ISP focus first
    if (latest.ping_ms > 120 && latest.download_mbps > 20) {
      return reorder(order, ["dns-switch", "isp-outage", "check-cables", "wifi-vs-wired", "reposition", "background-hogs", "call-isp"]);
    }
    // If upload very low → background hogs / cables first
    if (latest.upload_mbps < 1.5) {
      return reorder(order, ["background-hogs", "check-cables", "wifi-vs-wired", "reposition", "isp-outage", "dns-switch", "call-isp"]);
    }
    // If download low → Wi-Fi & cables first
    return reorder(order, ["check-cables", "wifi-vs-wired", "reposition", "background-hogs", "dns-switch", "isp-outage", "call-isp"]);
  }, [latest]);

  const doneCount = Object.values(completed).filter(Boolean).length;
  const progress = (doneCount / steps.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-10 lg:py-16">
      <header className="mb-8 sm:mb-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Fix my internet</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">A calm, 7-step checklist</h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
          Don't panic. Work your way down the list — most problems are fixed in the first two steps.
        </p>
      </header>

      {/* Latest test context */}
      <div className="glass rounded-xl p-5 mb-8 flex flex-wrap items-center gap-4" data-testid="fix-latest-banner">
        <Gauge className="w-5 h-5 text-[var(--cobalt-bright)]" />
        {latest ? (
          <>
            <div className="text-sm">
              <span className="text-[var(--text-dim)]">Last test: </span>
              <span className="text-white font-mono">{Number(latest.download_mbps).toFixed(1)} Mbps down</span>
              <span className="text-[var(--text-muted)]"> · </span>
              <span className="text-white font-mono">{Number(latest.upload_mbps).toFixed(1)} up</span>
              <span className="text-[var(--text-muted)]"> · </span>
              <span className="text-white font-mono">{Math.round(latest.ping_ms)} ms ping</span>
              <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${
                latest.verdict === "good" ? "bg-emerald-500/10 text-emerald-300" :
                latest.verdict === "fair" ? "bg-amber-500/10 text-amber-300" :
                "bg-rose-500/10 text-rose-300"
              }`}>{latest.verdict}</span>
            </div>
            <Link to="/speed-test" className="btn-ghost ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs" data-testid="fix-retest-link">
              <RefreshCw className="w-3.5 h-3.5" /> Re-test
            </Link>
          </>
        ) : (
          <>
            <div className="text-sm text-[var(--text-dim)]">No test results yet — run a test first for a tailored order.</div>
            <Link to="/speed-test" className="btn-cobalt ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs" data-testid="fix-run-test-link">
              <Gauge className="w-3.5 h-3.5" /> Run speed test
            </Link>
          </>
        )}
      </div>

      {/* Progress */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#4A78FF,#5C85FF)" }} />
        </div>
        <span className="text-xs font-mono text-[var(--text-muted)]" data-testid="fix-progress-count">{doneCount} / {steps.length}</span>
      </div>

      {/* Advanced toggle */}
      <div className="mb-4 flex items-center justify-end">
        <button
          onClick={() => setShowDetails((v) => !v)}
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--text-dim)] hover:text-white transition"
          data-testid="fix-toggle-advanced"
        >
          <Terminal className="w-3.5 h-3.5" />
          {showDetails ? "Hide advanced logs" : "Show advanced logs"}
        </button>
      </div>

      {/* Steps */}
      <ol className="space-y-3" data-testid="fix-steps-list">
        {steps.map((step, i) => {
          const done = !!completed[step.id];
          return (
            <li key={step.id} className={`glass rounded-xl p-5 lift ${done ? "opacity-60" : ""}`} data-testid={`fix-step-${step.id}`}>
              <button onClick={() => toggle(step.id)} className="w-full text-left flex items-start gap-4" data-testid={`fix-step-toggle-${step.id}`}>
                <div className="shrink-0 mt-0.5">
                  {done ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  ) : (
                    <div className="relative">
                      <Circle className="w-6 h-6 text-[var(--text-muted)]" />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-mono text-[var(--cobalt-bright)]">{i + 1}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-display text-base font-semibold ${done ? "line-through" : ""}`}>{step.title}</div>
                  <div className="text-sm text-[var(--text-dim)] mt-1 leading-relaxed">{step.quick}</div>
                  {showDetails && (
                    <div className="mt-3 p-3 rounded-md bg-black/40 border border-white/5 font-mono text-xs text-[var(--text-dim)] leading-relaxed">
                      {step.detail}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--text-muted)] shrink-0 mt-1" />
              </button>
            </li>
          );
        })}
      </ol>

      {doneCount === steps.length && (
        <div className="mt-8 glass-strong rounded-xl p-6 text-center" data-testid="fix-all-done">
          <div className="font-display text-xl font-semibold mb-2">Checklist complete.</div>
          <p className="text-sm text-[var(--text-dim)] mb-4">Give it another test — if things still aren't right, your ISP is the most likely culprit.</p>
          <Link to="/speed-test" className="btn-cobalt inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
            <Gauge className="w-4 h-4" /> Run speed test again
          </Link>
        </div>
      )}

      {/* Mobile advisor */}
      <div className="mt-8 sm:mt-10">
        <MobileAdvisor testid="fix-mobile-advisor" />
      </div>

      {/* Ad: bottom */}
      <div className="mt-8">
        <AdSlot slot={AD_SLOTS.fixMid} name="fix-mid" minHeight={120} />
      </div>
    </div>
  );
}

function reorder(original, order) {
  const map = Object.fromEntries(original.map((s) => [s.id, s]));
  return order.map((id) => map[id]).filter(Boolean);
}
